import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibAddFillIcon, KibEditIcon } from '@chewy/kib-icons-react'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import { Box, Grid, IconButton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  AddButton,
  type ClassesType,
  Defaults,
  LanguageUtils,
  moment,
  type Nil,
  TextInteractive,
} from '@pbt/pbt-ui-components'
import { Edit as EditIcon } from '@pbt/pbt-ui-components/src/icons'

import FeatureToggle from '~/constants/featureToggle'
import { fetchAppointment } from '~/store/actions/timetable'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getTimetableEvent } from '~/store/reducers/timetable'
import type { TimetableEvent } from '~/types'
import { addOriginalBusinessId } from '~/utils'

import Link from '../link/Link'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import styles from './AddToAppointmentCard.module.scss'

const useStyles = makeStyles(
  (theme) => ({
    appointmentInfo: {
      border: theme.constants.tableBorder,
    },
    editIcon: {
      color: theme.colors.brandPrimary,
      padding: theme.spacing(0.5),
    },
    addText: {
      fontWeight: 400,
    },
    link: {
      color: theme.colors.grayGray1,
    },
  }),
  { name: 'AddToAppointmentCard' },
)

export interface AddToAppointmentCardProps {
  appointmentId?: string | Nil
  appointmentProp?: TimetableEvent | Nil
  classes?: ClassesType<typeof useStyles>
  isChirp?: boolean
  onAddToAppointmentRequested: () => void
  onEdit?: () => void
  readOnly?: boolean
}

const AddToAppointmentCard = ({
  appointmentProp,
  appointmentId,
  classes: classesProp,
  onAddToAppointmentRequested,
  onEdit,
  readOnly,
  isChirp = false,
}: AddToAppointmentCardProps) => {
  const dispatch = useDispatch()
  const classes = useStyles({ classes: classesProp })
  const { t } = useTranslation(['Common', 'TimeTable'])

  const eventId = appointmentId || appointmentProp?.id
  const appointmentFromState = useSelector(getTimetableEvent(appointmentId))
  const appointment = appointmentProp || appointmentFromState
  const isPatientSharingEnabled = useSelector(
    getFeatureToggle(FeatureToggle.PATIENT_SHARING),
  )

  const appointmentTypeName = LanguageUtils.getTranslatedFieldName(
    appointment?.businessAppointmentType,
  )

  const isLoading = appointmentId !== undefined && !appointmentFromState

  useEffect(() => {
    if (isLoading && appointmentId) {
      dispatch(fetchAppointment(appointmentId))
    }
  }, [isLoading])

  const soaps = appointment?.soaps || []
  const firstSoapId = soaps.length > 0 ? soaps[0].id : undefined

  const text = `${appointmentTypeName} ${moment(
    appointment?.scheduledStartDatetime,
  ).format(Defaults.DATE_FORMAT)}`
  const soapLink = addOriginalBusinessId(
    `/soap/${firstSoapId}`,
    isPatientSharingEnabled ? appointment?.businessId : null,
  )

  if (isChirp) {
    return (
      <Box width="100%">
        {eventId ? (
          <Box
            alignItems="center"
            className={styles.chirpAppointmentInfo}
            display="flex"
            gap={2}
            px={2}
            py={1}
            width="100%"
          >
            {isLoading ? (
              <KibSkeletonText data-testid="kib-skeleton-text" />
            ) : (
              <span className={styles.chirpAddedToText}>
                <span>{t('Common:ADDED_TO')}: </span>
                {firstSoapId ? (
                  <RouterLink className={styles.chirpLink} to={soapLink}>
                    {text}
                  </RouterLink>
                ) : (
                  text
                )}
              </span>
            )}
            {!readOnly && (
              <KibButtonNew
                iconOnly
                aria-label={t(
                  'TimeTable:ADD_APPOINTMENT_COMPONENT.EDIT_APPOINTMENT',
                )}
                emphasis="secondary"
                icon={<KibEditIcon />}
                size="small"
                theme="utility"
                onClick={onEdit}
              />
            )}
          </Box>
        ) : (
          !readOnly && (
            <KibButtonNew
              className={styles.addButtonChirp}
              emphasis="tertiary"
              icon={<KibAddFillIcon />}
              size="small"
              theme="action"
              onClick={onAddToAppointmentRequested}
            >
              {t('Common:ADD_TO_APPOINTMENT')}
            </KibButtonNew>
          )
        )}
      </Box>
    )
  }

  return (
    <Grid container item>
      {eventId ? (
        <Grid
          container
          item
          alignItems="center"
          className={classes.appointmentInfo}
          px={1}
          py={0.5}
        >
          <Grid item xs>
            <TextInteractive isLoading={isLoading}>
              {t('Common:ADDED_TO')}:{' '}
              {firstSoapId ? (
                <Link className={classes.link} to={soapLink}>
                  {text}
                </Link>
              ) : (
                text
              )}
            </TextInteractive>
          </Grid>
          {!readOnly && (
            <IconButton
              aria-label={t('Common:EDIT_ACTION')}
              className={classes.editIcon}
              disabled={isLoading}
              size="large"
              onClick={onEdit}
            >
              <EditIcon />
            </IconButton>
          )}
        </Grid>
      ) : (
        !readOnly && (
          <AddButton
            addText={t('Common:ADD_TO_APPOINTMENT')}
            classes={{ addText: classes.addText }}
            onAdd={onAddToAppointmentRequested}
          />
        )
      )}
    </Grid>
  )
}

export default AddToAppointmentCard
